// Admin Catalog Board — the kanban surface that replaces the separate
// Categories and Products pages. Categories are columns; products are cards.
// Cards drag within a column and across columns (pointer + keyboard), and each
// drop persists the new order/category via the batch reorder endpoint. "Add
// Category" sits up top; each column adds/edits products. Editing opens the rich
// product editor (fields, icon, options, tiered pricing).
import {
  useEffect, useState, useRef, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
  pointerWithin, rectIntersection, closestCenter, getFirstCollision, MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove, sortableKeyboardCoordinates, SortableContext, horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box, Typography, Button, Alert, CircularProgress, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { fetchCatalog, reorderCatalog, applyReorder } from '../../store/admin-catalog-slice.js';
import { openProductEditor } from '../../store/product-editor-slice.js';
import {
  groupBoard, findContainer, reorderPayload, productDndId, categoryDndId,
} from '../../utils/board-dnd-helpers.js';
import CategoryColumn from '../../components/admin/catalog-board/CategoryColumn.jsx';
import { ProductCardPreview } from '../../components/admin/catalog-board/ProductCard.jsx';
import ProductEditorDialog from '../../components/admin/product-editor/ProductEditorDialog.jsx';
import OptionEditorDialog from '../../components/admin/OptionEditorDialog.jsx';
import CategoryFormDialog from '../../components/admin/CategoryFormDialog.jsx';

const EMPTY_CATEGORY_DIALOG = { open: false, category: null };

const AdminCatalogBoardPage = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.adminCatalog);
  const { categories, products } = data;

  const [board, setBoard] = useState({ order: [], items: {} });
  const [activeId, setActiveId] = useState(null);
  const startContainer = useRef(null);
  // Collision-detection memory for the multi-container product drag. When a card
  // moves to a new column the layout shifts and dnd-kit can momentarily report
  // `over` as null; without caching the last valid target it falls back to the
  // stale previous target, which yanks cards into wrong slots and leaves stuck
  // transforms. `recentlyMovedToNewContainer` pins the target to the dragged
  // card for one frame right after a cross-column move. (dnd-kit canonical
  // "multiple containers" pattern.)
  const lastOverId = useRef(null);
  const recentlyMovedToNewContainer = useRef(false);
  const [categoryDialog, setCategoryDialog] = useState(EMPTY_CATEGORY_DIALOG);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Custom collision detection optimized for the multi-column board.
  // Category drags resolve against the columns; product drags first look for the
  // pointer's intersecting droppable, refine a column hit down to the closest
  // card inside it, and fall back to the cached last target when a freshly moved
  // card briefly reports no collision.
  const collisionDetection = useCallback((args) => {
    if (activeId && String(activeId).startsWith('category-')) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (c) => String(c.id).startsWith('category-'),
        ),
      });
    }

    const pointerIntersections = pointerWithin(args);
    const intersections = pointerIntersections.length > 0
      ? pointerIntersections
      : rectIntersection(args);
    let overId = getFirstCollision(intersections, 'id');

    if (overId != null) {
      const overStr = String(overId);
      const isColumn = overStr.startsWith('category-') || overStr.startsWith('dropzone-');
      if (isColumn) {
        const containerId = findContainer(board.items, overId);
        const containerItems = board.items[containerId] || [];
        if (containerItems.length > 0) {
          const ids = new Set(containerItems.map((p) => productDndId(p.id)));
          const closest = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter(
              (c) => c.id !== overId && ids.has(String(c.id)),
            ),
          })[0]?.id;
          if (closest != null) overId = closest;
        }
      }
      lastOverId.current = overId;
      return [{ id: overId }];
    }

    if (recentlyMovedToNewContainer.current) {
      lastOverId.current = activeId;
    }
    return lastOverId.current ? [{ id: lastOverId.current }] : [];
  }, [activeId, board]);

  useEffect(() => { dispatch(fetchCatalog()); }, [dispatch]);

  // Re-derive the board from redux whenever the catalog changes, except mid-drag
  // (the local board is authoritative while the user is dragging).
  useEffect(() => {
    if (!activeId) setBoard(groupBoard(categories, products));
  }, [categories, products, activeId]);

  // Clear the "just moved containers" hint one frame after the board mutates so
  // collision detection resumes using live targets.
  useEffect(() => {
    const raf = requestAnimationFrame(() => { recentlyMovedToNewContainer.current = false; });
    return () => cancelAnimationFrame(raf);
  }, [board]);

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    if (String(active.id).startsWith('category-')) {
      startContainer.current = null;
      return;
    }
    startContainer.current = findContainer(board.items, active.id);
  };

  // Live preview: as the cursor moves, reshuffle the local board so the
  // dashed source column lands in the slot currently being hovered (and the
  // other columns shift aside). The DragOverlay shows the floating preview;
  // the source's sortable transform is suppressed in CategoryColumn so its
  // dashed slot stays exactly where we re-position it. Products move between
  // columns in the same way.
  const handleDragOver = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    if (String(active.id).startsWith('category-')) {
      const activeCatId = Number(String(active.id).split('-')[1]);
      const overIdRaw = String(over.id);
      let overCatId;
      if (overIdRaw.startsWith('category-') || overIdRaw.startsWith('dropzone-')) {
        overCatId = Number(overIdRaw.split('-')[1]);
      } else {
        overCatId = Number(findContainer(board.items, over.id));
      }
      if (!Number.isFinite(overCatId) || activeCatId === overCatId) return;
      setBoard((prev) => {
        const oldIndex = prev.order.indexOf(activeCatId);
        const newIndex = prev.order.indexOf(overCatId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
        return { ...prev, order: arrayMove(prev.order, oldIndex, newIndex) };
      });
      return;
    }

    setBoard((prev) => {
      const from = findContainer(prev.items, active.id);
      const to = findContainer(prev.items, over.id);
      // Same column: do nothing here. verticalListSortingStrategy animates the
      // cards aside live, and the final order is committed in handleDragEnd.
      // Mutating the array here too would double-shift every card and open gaps.
      if (from == null || to == null || from === to) return prev;
      const moving = prev.items[from].find((p) => productDndId(p.id) === active.id);
      if (!moving) return prev;

      // Cross column: pull the card out of its source list and splice it into
      // the target list. When hovering a card, drop above or below it depending
      // on the pointer position; when hovering the column itself, append.
      const overIsCard = String(over.id).startsWith('product-');
      const overIdx = overIsCard
        ? prev.items[to].findIndex((p) => productDndId(p.id) === over.id)
        : -1;
      let index;
      if (overIdx >= 0) {
        const isBelow = active.rect.current.translated
          && active.rect.current.translated.top > over.rect.top + over.rect.height / 2;
        index = overIdx + (isBelow ? 1 : 0);
      } else {
        index = prev.items[to].length;
      }
      const nextFrom = prev.items[from].filter((p) => p.id !== moving.id);
      const nextTo = [...prev.items[to].slice(0, index), moving, ...prev.items[to].slice(index)];
      recentlyMovedToNewContainer.current = true;
      return { ...prev, items: { ...prev.items, [from]: nextFrom, [to]: nextTo } };
    });
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    // Category drag: live-preview already reshuffled board.order; just persist.
    if (String(active.id).startsWith('category-')) {
      const payload = reorderPayload(board.items, [], board.order);
      if (payload.categories.length === 0) return;
      dispatch(applyReorder(payload));
      dispatch(reorderCatalog(payload));
      return;
    }

    // Product drag. Cross-column moves were applied live in handleDragOver;
    // same-column reordering is committed here via a final arrayMove so the
    // sorting strategy (not an array mutation) owns the in-flight animation.
    const container = findContainer(board.items, active.id);
    const overContainer = findContainer(board.items, over.id);
    if (container == null || overContainer == null) return;

    let next = board;
    if (container === overContainer && String(over.id).startsWith('product-')) {
      const list = board.items[container];
      const oldIndex = list.findIndex((p) => productDndId(p.id) === active.id);
      const newIndex = list.findIndex((p) => productDndId(p.id) === over.id);
      if (oldIndex !== newIndex && newIndex !== -1) {
        const moved = arrayMove(list, oldIndex, newIndex);
        next = { ...board, items: { ...board.items, [container]: moved } };
        setBoard(next);
      }
    }

    const affected = [...new Set([startContainer.current, container].filter((c) => c != null))];
    const payload = reorderPayload(next.items, affected);
    if (payload.products.length === 0) return;
    dispatch(applyReorder(payload));
    dispatch(reorderCatalog(payload));
  };

  const handleDragCancel = () => {
    // Drop aborted (ESC / released outside): discard any live cross-column
    // reshuffling by re-deriving the board from the persisted catalog.
    setBoard(groupBoard(categories, products));
    setActiveId(null);
    startContainer.current = null;
  };

  const editProduct = (p) => dispatch(openProductEditor({ product: p }));
  const addProduct = (id) => dispatch(openProductEditor({ defaultCategoryId: id }));
  const editCategory = (c) => setCategoryDialog({ open: true, category: c });

  if (loading && categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading catalog" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2,
      }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Catalog</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCategoryDialog({ open: true, category: null })}>
          Add category
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {categories.length === 0 && !loading && (
        <Alert severity="info">Create a category to start building the catalog.</Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Box sx={{
          display: 'flex', gap: 2, overflowX: 'auto', flex: 1, alignItems: 'flex-start', pb: 1,
        }}
        >
          <SortableContext
            items={board.order.map((id) => categoryDndId(id))}
            strategy={horizontalListSortingStrategy}
          >
            {board.order.map((catId) => {
              const category = categories.find((c) => c.id === catId);
              if (!category) return null;
              return (
                <CategoryColumn
                  key={catId}
                  category={category}
                  products={board.items[catId] || []}
                  onEditCategory={editCategory}
                  onAddProduct={addProduct}
                  onEditProduct={editProduct}
                />
              );
            })}
          </SortableContext>
        </Box>
        <DragOverlay dropAnimation={{ duration: 180 }}>
          {activeId && String(activeId).startsWith('category-') && (() => {
            const catId = Number(String(activeId).split('-')[1]);
            const category = categories.find((c) => c.id === catId);
            if (!category) return null;
            const count = (board.items[catId] || []).length;
            return (
              <Paper
                elevation={6}
                sx={{
                  width: 300,
                  p: 1.5,
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                  cursor: 'grabbing',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                  {category.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${count} ${count === 1 ? 'product' : 'products'}`}
                </Typography>
              </Paper>
            );
          })()}
          {activeId && String(activeId).startsWith('product-') && (() => {
            const productId = Number(String(activeId).split('-')[1]);
            const product = products.find((p) => p.id === productId);
            if (!product) return null;
            return <ProductCardPreview product={product} />;
          })()}
        </DragOverlay>
      </DndContext>

      <CategoryFormDialog
        open={categoryDialog.open}
        category={categoryDialog.category}
        onClose={() => setCategoryDialog(EMPTY_CATEGORY_DIALOG)}
      />
      <ProductEditorDialog />
      <OptionEditorDialog />
    </Box>
  );
};

export default AdminCatalogBoardPage;
